import React, { useEffect, useState } from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import ContactPicker from '../../shared/ContactPicker';
import PickSelect from '../../shared/PickSelect';
import { losApi } from '../../losApi';
import { fmt$, missingLabels } from '../constants';

// Shared pieces of the Form 1084 / 91 / 92 screens (ticket 215).
// Display formatting only: every number comes from the backend result.
export const money = (n) => (n == null ? '—' : fmt$(n));
export const pct = (n) => (n == null ? '—' : `${Number(n).toFixed(2)}%`);

export const MISSING_WRAP = 'rounded-md ring-2 ring-amber-300';

// Wraps an input the last calculation was refused for lacking.
export function MissingMark({ missing, children }) {
  if (!missing) return children;
  return <div className={MISSING_WRAP} data-testid="missing-input">{children}</div>;
}

export function MissingPanel({ result }) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="income-form-missing">
      <div className="text-sm font-semibold text-amber-900 flex items-center gap-2">
        <AlertTriangle size={14} /> Can&apos;t calculate — missing {missingLabels(result)}
      </div>
      <div className="text-xs text-amber-800 mt-1">Fill the outlined inputs, or enter 0 where the return shows zero.</div>
    </div>
  );
}

export function Warnings({ warnings }) {
  if (!warnings || warnings.length === 0) return null;
  return (
    <ul className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 space-y-1 list-disc pl-8" data-testid="income-form-warnings">
      {warnings.map((w, i) => <li key={i}>{w}</li>)}
    </ul>
  );
}

export function VersionLine({ def }) {
  return (
    <div className="text-xs text-slate-500" data-testid="income-form-version">
      Lines follow {def.publisher} {def.title} ({def.version}).{' '}
      {def.source_url && <a href={def.source_url} target="_blank" rel="noreferrer" className="underline">Official form</a>}
    </div>
  );
}

// Borrower name, links, Save / Update, Download PDF.
export function SaveBar({ formId, ctl }) {
  const f = ctl.forms[formId];
  const { draft, worksheet, dirty, busy } = f;
  const [loans, setLoans] = useState([]);

  useEffect(() => {
    if (!draft.contact_id) { setLoans([]); return undefined; }
    let live = true;
    losApi.listLoans({ contact_id: draft.contact_id })
      .then((rows) => { if (live) setLoans(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (live) setLoans([]); });
    return () => { live = false; };
  }, [draft.contact_id]);

  const loanLabel = (l) => [l.loan_number, l.property_address?.street].filter(Boolean).join(' · ') || l.loan_file_id;
  const options = [{ value: '', label: 'No loan' }, ...loans.map((l) => ({ value: l.loan_file_id, label: loanLabel(l) }))];
  if (draft.loan_file_id && !loans.some((l) => l.loan_file_id === draft.loan_file_id)) {
    options.push({ value: draft.loan_file_id, label: 'Linked loan' });
  }

  let downloadHint = '';
  if (!worksheet) downloadHint = 'Save the worksheet first.';
  else if (dirty) downloadHint = 'Update the worksheet first: it has unsaved changes.';
  else if (!worksheet.complete) downloadHint = 'The worksheet is incomplete: fill the missing inputs and update it.';

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="income-form-save-bar">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-slate-500">Borrower name</Label>
          <Input
            value={draft.borrower_name}
            onChange={(e) => ctl.setMeta(formId, { borrower_name: e.target.value })}
            maxLength={120}
            className="mt-1 h-9 text-sm"
            data-testid="income-form-borrower"
          />
        </div>
        <ContactPicker
          value={draft.contact_id}
          onChange={(id) => ctl.pickContact(formId, id)}
          label="Contact"
          testId="income-form-contact"
        />
        <PickSelect
          label="Loan (optional)"
          value={draft.loan_file_id || ''}
          onChange={(v) => ctl.setMeta(formId, { loan_file_id: v || null })}
          options={options}
          testId="income-form-loan"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => ctl.save(formId)} disabled={busy} variant="navy" data-testid="income-form-save">
          {worksheet ? 'Update worksheet' : 'Save worksheet'}
        </Button>
        <span title={downloadHint || undefined}>
          <Button
            onClick={() => ctl.download(worksheet.id)}
            disabled={!!downloadHint}
            variant="outline"
            data-testid="income-form-download"
          >
            <Download size={14} /> Download PDF
          </Button>
        </span>
        {downloadHint && <span className="text-xs text-slate-500" data-testid="income-form-download-hint">{downloadHint}</span>}
      </div>
    </div>
  );
}
